// Header pill + dropdown listing recently SHIPPED changelog entries so agents
// notice new things without hunting for them.
//
// Data source: the live `claude_shipped` changelog feed (feedback_requests),
// which the GHA deploy pipeline auto-populates from git commits on every push
// to prod. Per-user "seen" state lives server-side in feedback_request_views,
// so the unread count is consistent across devices and matches the Changelog
// page + sidebar badge exactly.
//
// (The separate inline FeatureSpotlight system — pulsing rings on actual UI
// elements, backed by featureSpotlightRegistry.js — still powers WhatsNewSection
// below and is unrelated to this pill.)
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Sparkles, ArrowRight, Check, ArrowUpRight, CheckCheck, BellOff, Bell } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { classify, moduleColor } from '../utils/changelogClassify';
import {
  getVisibleSpotlights,
  isSeen,
  markSeen,
  requestForceShow,
  isSpotlightsDisabled,
  setSpotlightsDisabled,
  ensureSeenLoaded,
  subscribeSeen,
} from '../featureSpotlightRegistry';

// How many entries to show in the dropdown (the full list lives on /changelog).
const MAX_ROWS = 20;

export default function WhatsNewPopover() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth() || {};
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [viewedIds, setViewedIds] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const btnRef = useRef(null);

  const isClient = user?.role === 'client';

  const fetchData = useCallback(() => {
    if (!user || isClient) return;
    let cancelled = false;
    Promise.all([
      api.get('/feedback', { params: { source: 'claude_shipped' } }),
      api.get('/feedback/_/views').catch(() => ({ data: { ids: [] } })),
    ])
      .then(([list, views]) => {
        if (cancelled) return;
        setItems(Array.isArray(list.data) ? list.data : []);
        setViewedIds(new Set(views.data?.ids || []));
      })
      .catch(() => { /* lacks feedback module / offline — pill stays hidden */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user, isClient]);

  // Initial load + refetch when navigating (a new deploy may have landed) and
  // when the tab regains focus.
  useEffect(() => fetchData(), [fetchData, location.pathname]);
  useEffect(() => {
    const onFocus = () => fetchData();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchData]);

  const rows = useMemo(
    () => items.slice(0, MAX_ROWS).map((it) => ({ ...it, seen: viewedIds.has(it.id) })),
    [items, viewedIds]
  );

  const unseenCount = items.reduce((n, it) => n + (viewedIds.has(it.id) ? 0 : 1), 0);

  const markViewed = useCallback((id) => {
    setViewedIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    api.post(`/feedback/${id}/view`).catch(() => { /* non-fatal */ });
  }, []);

  const markAll = useCallback((e) => {
    e?.stopPropagation();
    setViewedIds(new Set(items.map((it) => it.id)));
    api.post('/feedback/_/views/mark-all').catch(() => { /* non-fatal */ });
  }, [items]);

  const tryIt = useCallback((item) => {
    markViewed(item.id);
    setOpen(false);
    const { route } = classify(item.title);
    navigate(route || '/changelog');
  }, [navigate, markViewed]);

  const dismissItem = useCallback((item, e) => {
    e.stopPropagation();
    markViewed(item.id);
  }, [markViewed]);

  if (!user || isClient) return null;
  if (loading || items.length === 0) return null;

  const pillStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 10px',
    borderRadius: 999,
    background: unseenCount > 0 ? 'var(--c-brand)' : 'var(--c-surface-2)',
    color: unseenCount > 0 ? '#fff' : 'var(--c-text-muted)',
    border: '1px solid ' + (unseenCount > 0 ? 'var(--c-brand)' : 'var(--c-border)'),
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    userSelect: 'none',
    lineHeight: 1,
  };

  return (
    <div style={{ position: 'relative' }}>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={pillStyle}
        title={t('spotlight.whatsNewTooltip')}
        aria-label={t('spotlight.whatsNewTooltip')}
      >
        <Sparkles size={13} />
        {unseenCount > 0 ? (
          <span>{unseenCount} {unseenCount === 1 ? t('spotlight.newSingular') : t('spotlight.newPlural')}</span>
        ) : (
          <span>{t('spotlight.whatsNew')}</span>
        )}
      </button>

      {open && (
        <>
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 30 }}
            onMouseDown={(e) => { if (e.target === e.currentTarget) { (() => setOpen(false))(e); } }}
          />
          <div
            style={{
              position: 'absolute',
              right: 0,
              top: 'calc(100% + 8px)',
              minWidth: 340,
              maxWidth: 380,
              maxHeight: 480,
              overflowY: 'auto',
              background: 'var(--c-surface)',
              border: '1px solid var(--c-border)',
              borderRadius: 'var(--radius-lg)',
              boxShadow: 'var(--shadow-lg)',
              zIndex: 40,
            }}
          >
            <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--c-border)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Sparkles size={15} style={{ color: 'var(--c-brand)' }} />
              <div style={{ fontSize: 13, fontWeight: 600, flex: 1 }}>{t('spotlight.whatsNew')}</div>
              <div style={{ fontSize: 11, color: 'var(--c-text-muted)' }}>
                {unseenCount > 0
                  ? t('spotlight.unseenCount', { count: unseenCount })
                  : t('spotlight.allCaughtUp')}
              </div>
              {unseenCount > 0 && (
                <button
                  type="button"
                  onClick={markAll}
                  className="btn btn-ghost btn-icon"
                  title={t('changelog.markAllSeen')}
                  style={{ padding: 2 }}
                >
                  <CheckCheck size={13} />
                </button>
              )}
            </div>

            {rows.map((item) => (
              <ChangelogRow
                key={item.id}
                item={item}
                onTry={() => tryIt(item)}
                onDismiss={(e) => dismissItem(item, e)}
                t={t}
              />
            ))}

            <div style={{ padding: '8px 14px', borderTop: '1px solid var(--c-border)', textAlign: 'right' }}>
              <button
                type="button"
                onClick={() => { setOpen(false); navigate('/changelog'); }}
                className="btn btn-ghost btn-sm"
              >
                {t('spotlight.seeFullChangelog')} <ArrowRight size={12} />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// A single shipped-changelog entry inside the header dropdown.
function ChangelogRow({ item, onTry, onDismiss, t }) {
  const { label: mod, route, rest } = classify(item.title);
  const color = moduleColor(mod);
  return (
    <div
      style={{
        padding: '12px 14px',
        borderBottom: '1px solid var(--c-border)',
        opacity: item.seen ? 0.7 : 1,
        background: item.seen ? 'transparent' : 'var(--c-surface-2)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, flexWrap: 'wrap' }}>
            {!item.seen && (
              <span style={{
                fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
                background: 'var(--c-brand)', color: '#fff', letterSpacing: 0.5,
              }}>
                {t('changelog.new')}
              </span>
            )}
            {mod && (
              <span style={{
                fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                background: color.bg, color: color.fg, whiteSpace: 'nowrap',
              }}>
                {mod}
              </span>
            )}
          </div>
          <div style={{ fontSize: 13, fontWeight: item.seen ? 400 : 500, lineHeight: 1.35, color: 'var(--c-text)' }}>
            {rest}
          </div>
        </div>
        {!item.seen && (
          <button
            type="button"
            onClick={onDismiss}
            className="btn btn-ghost btn-icon"
            title={t('spotlight.markAsSeen')}
            style={{ padding: 2, flexShrink: 0 }}
          >
            <Check size={14} />
          </button>
        )}
      </div>
      {route && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
          <button
            type="button"
            onClick={onTry}
            className={item.seen ? 'btn btn-ghost btn-sm' : 'btn btn-primary btn-sm'}
          >
            {t('changelog.try')} <ArrowUpRight size={12} />
          </button>
        </div>
      )}
    </div>
  );
}

function SpotlightRow({ item, onTry, onDismiss, t }) {
  const title = t(item.titleKey, item.titleKey);
  const body = t(item.bodyKey, item.bodyKey);
  const moduleLabel = item.moduleKey ? t(`nav.${item.moduleKey}`, item.moduleKey) : '';

  return (
    <div
      style={{
        padding: '12px 14px',
        borderBottom: '1px solid var(--c-border)',
        opacity: item.seen ? 0.7 : 1,
        background: item.seen ? 'transparent' : 'var(--c-surface-2)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2, flexWrap: 'wrap' }}>
            {!item.seen && (
              <span style={{
                fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
                background: 'var(--c-brand)', color: '#fff', letterSpacing: 0.5,
              }}>
                {t('changelog.new')}
              </span>
            )}
            {moduleLabel && (
              <span style={{
                fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 10,
                background: 'var(--c-surface)', color: 'var(--c-text-muted)',
                border: '1px solid var(--c-border)', textTransform: 'uppercase', letterSpacing: 0.3,
              }}>
                {moduleLabel}
              </span>
            )}
          </div>
          <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.3, marginBottom: 3 }}>{title}</div>
          <div style={{ fontSize: 12, color: 'var(--c-text-muted)', lineHeight: 1.4 }}>{body}</div>
        </div>
        {!item.seen && (
          <button
            type="button"
            onClick={onDismiss}
            className="btn btn-ghost btn-icon"
            title={t('spotlight.markAsSeen')}
            style={{ padding: 2, flexShrink: 0 }}
          >
            <Check size={14} />
          </button>
        )}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={onTry}
          className={item.seen ? 'btn btn-ghost btn-sm' : 'btn btn-primary btn-sm'}
        >
          {item.seen ? t('spotlight.showMeAgain') : t('spotlight.tryIt')} <ArrowRight size={12} />
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Smaller embeddable variant used inside the Changelog page.
// ─────────────────────────────────────────────────────────────────────
export function WhatsNewSection() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth() || {};
  const [tick, setTick] = useState(0);

  // Load the user's server-side seen state once, and re-render when it (or any
  // dismissal from an inline spotlight) changes so this card stays in sync.
  useEffect(() => { ensureSeenLoaded(user?.id); }, [user?.id]);
  useEffect(() => subscribeSeen(() => setTick((n) => n + 1)), []);

  useEffect(() => {
    const onFocus = () => setTick((n) => n + 1);
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);
  useEffect(() => { setTick((n) => n + 1); }, [location.pathname]);

  const items = useMemo(() => {
    void tick;
    const visible = getVisibleSpotlights(user);
    return visible.map((s) => ({ ...s, seen: isSeen(s.key, user?.id) }));
  }, [user, tick]);

  const disabled = useMemo(() => {
    void tick;
    return isSpotlightsDisabled(user?.id);
  }, [user?.id, tick]);

  const togglePause = useCallback(() => {
    setSpotlightsDisabled(user?.id, !disabled);
    setTick((n) => n + 1);
  }, [user?.id, disabled]);

  if (!user || user.role === 'client' || items.length === 0) return null;

  const tryIt = (item) => {
    requestForceShow(item.key);
    navigate(item.path);
  };
  const dismissItem = (item, e) => {
    e.stopPropagation();
    markSeen(item.key, user?.id);
    setTick((n) => n + 1);
  };
  const unseenCount = disabled ? 0 : items.filter((i) => !i.seen).length;

  return (
    <div className="card" style={{ marginBottom: 24, padding: 0, overflow: 'hidden' }}>
      <div style={{ padding: '12px 14px', borderBottom: disabled ? 'none' : '1px solid var(--c-border)', display: 'flex', alignItems: 'center', gap: 8, background: 'var(--c-surface-2)' }}>
        <Sparkles size={16} style={{ color: disabled ? 'var(--c-text-muted)' : 'var(--c-brand)' }} />
        <div style={{ fontSize: 14, fontWeight: 600, flex: 1 }}>
          {disabled ? t('spotlight.pausedTitle') : t('spotlight.tryTheseTitle')}
        </div>
        <div style={{ fontSize: 11, color: 'var(--c-text-muted)' }}>
          {!disabled && (unseenCount > 0
            ? t('spotlight.unseenCount', { count: unseenCount })
            : t('spotlight.allCaughtUp'))}
        </div>
        <button
          type="button"
          onClick={togglePause}
          className={disabled ? 'btn btn-primary btn-sm' : 'btn btn-ghost btn-sm'}
          title={disabled ? t('spotlight.resumeTitle') : t('spotlight.pauseTitle')}
        >
          {disabled ? <><Bell size={13} /> {t('spotlight.resume')}</> : <><BellOff size={13} /> {t('spotlight.pause')}</>}
        </button>
      </div>
      {disabled && (
        <div style={{ padding: '14px 16px', fontSize: 13, color: 'var(--c-text-muted)' }}>
          {t('spotlight.pausedBody')}
        </div>
      )}
      {!disabled && items.map((item) => (
        <SpotlightRow
          key={item.key}
          item={item}
          onTry={() => tryIt(item)}
          onDismiss={(e) => dismissItem(item, e)}
          t={t}
        />
      ))}
    </div>
  );
}
